/**
 * useAvailableMetrics Hook (Story 5.35, Task 6)
 *
 * Fetches metric/dimension schemas for all of a client's connected integrations
 * in parallel, merging them into a unified AvailableDataSources object.
 *
 * Uses useClientIntegrations to get connected platforms, then useQueries
 * to fetch schemas for each platform concurrently.
 */

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useClientIntegrations } from "./useClientIntegrations";
import { apiGet } from "../lib/apiClient";
import type {
  MetricDefinition,
  DimensionDefinition,
  IntegrationMetricRegistry,
} from "@provenleap/integrations";

/**
 * Aggregated data sources from all connected integrations for a client.
 */
export interface AvailableDataSources {
  /** Registries indexed by platform ID */
  byIntegration: Record<string, IntegrationMetricRegistry>;
  /** All metrics from all connected integrations, tagged with platform */
  allMetrics: Array<MetricDefinition & { platform: string }>;
  /** All dimensions from all connected integrations, tagged with platform */
  allDimensions: Array<DimensionDefinition & { platform: string }>;
  /** True while any schema query is still loading */
  isLoading: boolean;
}

/**
 * Fetches and merges metric/dimension schemas for all of a client's
 * connected integrations. Returns empty data when clientId is undefined
 * or no integrations are connected.
 *
 * @param clientId - The client ID to fetch integrations for
 * @returns Merged AvailableDataSources with metrics, dimensions, and loading state
 */
export function useAvailableMetrics(
  clientId: string | undefined,
): AvailableDataSources {
  const { connectedPlatforms, isLoading: integrationsLoading } =
    useClientIntegrations(clientId);

  const schemaQueries = useQueries({
    queries: connectedPlatforms.map((platform) => ({
      queryKey: ["integration-schema", platform],
      queryFn: async () => {
        const res = await apiGet(`/api/integrations/${platform}/schema`);
        if (!res.ok) {
          throw new Error(`Failed to fetch schema for ${platform}`);
        }
        const json = await res.json();
        return json.data as IntegrationMetricRegistry;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!clientId && connectedPlatforms.length > 0,
    })),
  });

  const isLoading =
    integrationsLoading || schemaQueries.some((q) => q.isLoading);

  // Extract resolved registries into a stable array for memoization.
  // useQueries returns a new array reference every render, so we derive
  // a stable key from the resolved platform names to avoid re-computation.
  const registries = schemaQueries
    .map((q) => q.data)
    .filter((d): d is IntegrationMetricRegistry => d != null);
  const registryKey = registries.map((r) => r.platform).join(",");

  const result = useMemo(() => {
    const byIntegration: Record<string, IntegrationMetricRegistry> = {};
    const allMetrics: Array<MetricDefinition & { platform: string }> = [];
    const allDimensions: Array<DimensionDefinition & { platform: string }> = [];

    for (const registry of registries) {
      byIntegration[registry.platform] = registry;

      for (const metric of registry.metrics) {
        allMetrics.push({ ...metric, platform: registry.platform });
      }
      for (const dimension of registry.dimensions) {
        allDimensions.push({ ...dimension, platform: registry.platform });
      }
    }

    return { byIntegration, allMetrics, allDimensions };
  }, [registryKey]); // stable string key, changes only when query data resolves

  return {
    ...result,
    isLoading,
  };
}
